import React, { useState } from 'react'
import { Badge, BlockStack, Box, Button, Card, Divider, Icon, Image, InlineGrid, InlineStack, Link, List, Text } from '@shopify/polaris';
import { ImportIcon, PlayIcon, PlusIcon } from '@shopify/polaris-icons';
import { useDispatch, useSelector } from 'react-redux';
import shippingRatesEmptyState from "../../images/shipping-rates-empty-state.png";
import { checkEnabled } from '../../helper';
import useAuthenticatedFetch from '../../hooks/useAuthenticatedFetch';
import { setOpenWidget } from '../../store/actions/shippingActions';
import { setToast } from '../../store/actions/configActions';
import constants from '../../store/constants';

const CCS_VIDEO = "https://www.youtube.com/watch?v=nR34raPgyt8&list=PLkeebmoFziPwdGytDjJs51GuB3_OUzj5Q&index=2"

const SETUP_STEPS = [
    { title: 'Choose a delivery area', text: 'All locations, or specific countries, states, cities or postal codes.' },
    { title: 'Set the price', text: 'A flat rate, or ranges by cart value, weight or item quantity.' },
    { title: 'Go live at checkout', text: 'Customers see the rate as soon as it is active.' }
]
const CCS_STEPS = [
    { title: 'Contact Shopify Support', text: 'Ask Shopify to activate Carrier Calculated Shipping on your store.' },
    { title: 'Enable CCS', text: 'Follow the step-by-step video if you get stuck.' },
    { title: 'Come back and continue', text: 'Click Enable & Continue to start using the app.' }
]

const IconTile = ({ source, background, tone, iconColor }) => (
    <Box background={background} borderRadius="300" padding="300" width="48px">
        <span style={iconColor ? { color: iconColor } : undefined}>
            <Icon source={source} tone={tone} />
        </span>
    </Box>
)

const NoShipping = () => {
    const dispatch = useDispatch()
    const fetchJson = useAuthenticatedFetch()
    const { features, shop_id, warning } = useSelector(state => state.ConfigReducer)
    const [checking, setChecking] = useState(false)
    const csv = features.indexOf(constants.CSV) >= 0
    const steps = warning ? CCS_STEPS : SETUP_STEPS

    const openImport = () => {
        if (csv) {
            dispatch(setOpenWidget(true))
        } else {
            dispatch(setToast({ active: true, error: true, content: 'This feature is not available in current plan.' }))
        }
    }

    const options = [
        { title: 'Add a rate manually', text: 'Pick an area and set a flat rate or price ranges.', cta: 'Setup Shipping Rates', icon: PlusIcon, background: 'bg-fill-brand', tone: 'inherit', iconColor: '#fff', url: '/rates/new' },
        { title: 'Import from CSV', text: 'Upload many rates at once from a spreadsheet.', cta: 'Import', icon: ImportIcon, background: 'bg-fill-info-secondary', tone: 'info', onClick: openImport },
        { title: 'Watch a tutorial', text: 'See how to add a shipping rate step by step.', cta: 'Open tutorials', icon: PlayIcon, background: 'bg-fill-critical-secondary', tone: 'critical', url: '/tutorials' }
    ]

    return (
        <BlockStack gap="500">
            <Card padding="0">
                <InlineGrid columns={{ xs: 1, md: ["oneThird", "twoThirds"] }}>
                    <Box background="bg-surface-secondary" padding="600" minHeight="200px">
                        <InlineStack align="center" blockAlign="center">
                            <Image source={shippingRatesEmptyState} alt="" width={200} />
                        </InlineStack>
                    </Box>

                    <Box padding={{ xs: '500', md: '800' }}>
                        <BlockStack gap="500">
                            {warning ? (
                                <BlockStack gap="300" inlineAlign="start">
                                    <Badge tone="critical">Action required</Badge>
                                    <Text as="h2" variant="headingLg">Activate Carrier Calculated Shipping to Use This App</Text>
                                    <Text as="p">
                                        To use real-time carrier rates, you need to activate <Text as="span" fontWeight="semibold">Carrier Calculated Shipping (CCS)</Text> in your Shopify <Text as="span" fontWeight="semibold">settings</Text>. Contact <Link url="https://help.shopify.com/en/questions#/contact" external>Shopify Support</Link> to enable CCS.
                                    </Text>
                                    <InlineStack gap="200">
                                        <Button url={CCS_VIDEO} external tone="critical" variant="primary">How to enable CCS</Button>
                                        <Button loading={checking} disabled={checking} onClick={() => checkEnabled(shop_id, setChecking, dispatch, fetchJson)}>Enable &amp; Continue</Button>
                                    </InlineStack>
                                </BlockStack>
                            ) : (
                                <BlockStack gap="300">
                                    <Text as="h2" variant="headingLg">Set Up Shipping Rates for Accurate Checkout Pricing</Text>
                                    <Text as="p" tone="subdued">Add shipping rates so your customers see correct delivery options and costs at checkout.</Text>
                                </BlockStack>
                            )}

                            <BlockStack gap="200">
                                <Text as="h3" variant="headingSm">How it works</Text>
                                <List type="number" gap="loose">
                                    {steps.map(step => (
                                        <List.Item key={step.title}>
                                            <Text as="span" fontWeight="semibold">{step.title}</Text>
                                            <Text as="p" tone="subdued">{step.text}</Text>
                                        </List.Item>
                                    ))}
                                </List>
                            </BlockStack>

                            {!warning ? (
                                <InlineStack>
                                    <Button variant="primary" size="large" url="/rates/new">Setup Shipping Rates</Button>
                                </InlineStack>
                            ) : null}
                        </BlockStack>
                    </Box>
                </InlineGrid>
            </Card>

            <div style={{ opacity: warning ? 0.55 : 1 }}>
                <InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
                    {options.map(option => (
                        <Card key={option.title}>
                            <BlockStack gap="300">
                                <IconTile source={option.icon} background={option.background} tone={option.tone} iconColor={option.iconColor} />
                                <BlockStack gap="100">
                                    <Text as="h3" variant="headingMd">{option.title}</Text>
                                    <Text as="p" tone="subdued">{option.text}</Text>
                                </BlockStack>
                                <InlineStack>
                                    <Button variant="plain" disabled={Boolean(warning)} url={option.url} onClick={option.onClick}>{option.cta}</Button>
                                </InlineStack>
                            </BlockStack>
                        </Card>
                    ))}
                </InlineGrid>
            </div>
        </BlockStack>
    )
}

export default NoShipping;
